import { useEffect, useMemo, useState, type ClipboardEvent, type FormEvent, type ReactNode } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js"
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import {
  ArrowRight,
  Bell,
  BriefcaseBusiness,
  Camera,
  ChevronLeft,
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogOut,
} from "lucide-react"
import type { User } from "@pishgam/contracts"

import { api, monthName } from "./api"
import {
  DataCard,
  EmptyState,
  LoadingCard,
  Metric,
  Page,
  fiscalYearFor,
  fiscalYearLabel,
  fiscalYearOptions,
  formatMonthYear,
  messageFor,
  moneyInMillions,
  roleLabel,
  submit,
  ProfilePictureUpload,
  type Dashboard,
  type MoneyFigure,
} from "./shared"
import { EntitySwitch, useEntity } from "@/components/entity-switch"
import { PlanDonut } from "@/components/plan-donut"
import { comparablePeriod, MonthlyProfitChart, PnlScorecard, ProfitBridge } from "@/components/pnl-charts"
import { AdminUsers, ReissueCredentials } from "./pages/admin-users"
import { FinanceEntryPage } from "./pages/finance-entry"
import { PayrollPage } from "./pages/payroll"
import { fiscalChip, fiscalYears, MonthPicker, YearPicker } from "@/components/period-picker"
import { monthLabel, sheet, SheetTable } from "@/components/spreadsheet"
import { cn } from "@/lib/utils"
import { HomePage } from "./pages/home"
import { AllocationPage, CurrentAllocation } from "./pages/allocation"
import { ApprovalPage } from "./pages/approvals"
import { CloseMonitorPage } from "./pages/close-monitor"
import { UnitForecastPage } from "./pages/unit-forecast"
import { AllocationHistoryPage } from "./pages/allocation-history"
import { OrganizationAdmin, ProcessSettingsAdmin } from "./pages/admin-org"
import pishgamLogo from "@/assets/pishgam-ventures.jpg"
import { AppSidebar, currentNav } from "@/components/app-sidebar"
import { fa, type TasksResponse } from "./process"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/sidebar"
import { TooltipProvider } from "@/components/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend)
ChartJS.defaults.font.family = 'DanaFaNum, "Dana FaNum", Vazirmatn, Tahoma, sans-serif'

type Organization = {
  businessUnits: Array<{ id: number; nameFa: string; sortOrder: number; section: "fs" | "inv"; headId: number | null; isActive: boolean; reportingEntity: "pishgam" | "faraz" }>
  divisions: Array<{ id: number; nameFa: string }>
}

type Allocation = {
  id: number
  jalaliYear: number
  jalaliMonth: number
  status: string
  notes: string
  rejectionReason?: string
  lines: Array<{ id: number; businessUnitId: number; percentage: string; businessUnit: { nameFa: string } }>
  employee?: { id: number; nameFa: string }
  history?: Array<{ id: number; action: string; notes?: string; timestamp: string; actor?: { nameFa: string } }>
}

type FpaAnalysis = {
  fiscalYear: number
  round: number
  availablePlans: number[]
  consolidated: {
    months: Array<{ year: number; month: number; label: string; scenario: string; figure: MoneyFigure }>
    fy: MoneyFigure
    fyRealized: MoneyFigure
    fyForecast: MoneyFigure
    breakevenLabel: string | null
  }
  bus: Array<{
    businessUnitId: number
    businessUnitName: string
    fy: MoneyFigure
    fyRealized: MoneyFigure
    fyForecast: MoneyFigure
  }>
}

type PnlFiscalYear = {
  fiscalYear: number
  round: number
  rows: Array<{
    year: number
    month: number
    label: string
    comparable: boolean
    actual: MoneyFigure
    forecast: MoneyFigure
    variance: MoneyFigure
  }>
  totals: {
    actual: MoneyFigure
    forecast: MoneyFigure
    variance: MoneyFigure
    varianceActual: MoneyFigure
    varianceForecast: MoneyFigure
  }
}

const ALLOCATION_MONTHS = [
  { year: 1404, month: 6 },
  { year: 1404, month: 7 },
  { year: 1404, month: 8 },
  { year: 1404, month: 9 },
  { year: 1404, month: 10 },
  { year: 1404, month: 11 },
  { year: 1404, month: 12 },
  { year: 1405, month: 1 },
  { year: 1405, month: 2 },
  { year: 1405, month: 3 },
  { year: 1405, month: 4 },
  { year: 1405, month: 5 },
  { year: 1405, month: 6 },
]

const statusLabel: Record<string, string> = {
  draft: "پیش‌نویس",
  submitted: "ارسال‌شده",
  div_approved: "تأیید واحد",
  approved: "تأیید نهایی",
  rejected: "نیازمند اصلاح",
}

export function App() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<User>("/auth/me"), retry: false })

  if (me.isLoading) return <Splash />

  return (
    <TooltipProvider>
      <Routes>
        <Route path="/login" element={me.data ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
        <Route path="/*" element={me.data ? <Shell user={me.data} /> : <Navigate to="/login" replace />} />
      </Routes>
    </TooltipProvider>
  )
}

function Splash() {
  return (
    <main className="grid min-h-svh place-items-center bg-background">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        در حال بارگذاری سامانه
      </div>
    </main>
  )
}

// Text copied from Excel, Word or a chat carries invisible direction marks and line breaks.
const COPY_MARKS = /[\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g
const cleanUsername = (value: string) => value.replace(COPY_MARKS, "").replace(/\s+/g, "")
const cleanPassword = (value: string) => value.replace(COPY_MARKS, "").replace(/[\r\n\t]/g, "")

/** A pasted block holding both fields (the «کپی» text of a credential, or a copied Excel row). */
function splitCredentials(text: string): { username: string; password: string } | null {
  const clean = text.replace(COPY_MARKS, "")
  const user = clean.match(/نام کاربری\s*[:：]\s*(\S+)/)
  const pass = clean.match(/رمز(?:\s*عبور)?\s*[:：]\s*(\S+)/)
  if (user && pass) return { username: user[1]!, password: pass[1]! }
  const parts = clean.split(/[\t\r\n]+/).map((part) => part.trim()).filter(Boolean)
  const at = parts.findIndex((part) => /^[a-z][a-z0-9._-]{2,39}$/i.test(part))
  return at >= 0 && at < parts.length - 1 ? { username: parts[at]!, password: parts[at + 1]! } : null
}

function LoginPage() {
  const query = useQueryClient()
  const navigate = useNavigate()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const pasteBoth = (event: ClipboardEvent<HTMLInputElement>) => {
    const both = splitCredentials(event.clipboardData.getData("text"))
    if (!both) return
    event.preventDefault()
    setUsername(cleanUsername(both.username))
    setPassword(cleanPassword(both.password))
  }
  const login = useMutation({
    mutationFn: () => api<User>("/auth/login", { method: "POST", body: JSON.stringify({ username: cleanUsername(username), password: cleanPassword(password) }) }),
    onSuccess: (user) => {
      query.setQueryData(["me"], user)
      navigate("/dashboard")
    },
  })

  return (
    <main dir="rtl" lang="fa" className="grid min-h-svh lg:grid-cols-2">
      <section className="flex flex-col gap-4 p-6 md:p-10">
        <NavLink to="/login" className="flex w-fit items-center gap-2.5 font-semibold">
          <img src={pishgamLogo} alt="" className="size-8 rounded-md border object-cover" />
          سامانه برنامه و بودجه پیشگام
        </NavLink>
        <div className="flex flex-1 items-center justify-center">
          <form className="w-full max-w-xs" onSubmit={(event) => submit(event, () => login.mutate())}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-1 text-center">
                <h1 className="text-2xl font-bold">ورود به حساب کاربری</h1>
                <p className="text-balance text-sm text-muted-foreground">برای ادامه، نام کاربری و رمز عبور خود را وارد کنید.</p>
              </div>
              <Field>
                <FieldLabel htmlFor="username">نام کاربری</FieldLabel>
                <Input id="username" dir="ltr" className="text-left" autoFocus autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={username} onPaste={pasteBoth} onChange={(event) => setUsername(cleanUsername(event.target.value))} />
              </Field>
              <Field>
                <FieldLabel htmlFor="password">رمز عبور</FieldLabel>
                <div className="relative">
                  <Input id="password" dir="ltr" className="pe-10 text-left" type={showPassword ? "text" : "password"} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={password} onPaste={pasteBoth} onChange={(event) => setPassword(cleanPassword(event.target.value))} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground"
                    aria-label={showPassword ? "پنهان کردن رمز عبور" : "نمایش رمز عبور"}
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              </Field>
              {login.error && <FieldError>{messageFor(login.error)}</FieldError>}
              <Field>
                <Button type="submit" disabled={login.isPending || !username || !password}>
                  {login.isPending && <Loader2 className="size-4 animate-spin" />}
                  ورود
                </Button>
              </Field>
              <FieldSeparator />
              <FieldDescription className="text-center">فضای کاری امن مدیریت مالی</FieldDescription>
            </FieldGroup>
          </form>
        </div>
      </section>
      <section className="relative hidden bg-muted lg:block">
        <div className="absolute inset-10 grid place-items-center rounded-lg border bg-background/70">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-6 flex size-36 items-center justify-center rounded-[2rem] border bg-background p-3 shadow-md ring-8 ring-primary/10">
              <img src={pishgamLogo} alt="لوگوی پیشگام" className="size-full rounded-3xl object-cover" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight">تصمیم‌سازی مالی، منظم و قابل اتکا.</h2>
            <p className="mt-3 text-sm leading-7 text-muted-foreground">سامانه‌ای یکپارچه برای برنامه‌ریزی بودجه، پایش عملکرد، مدیریت تخصیص‌ها و گزارش‌های مالی پیشگام.</p>
          </div>
        </div>
      </section>
    </main>
  )
}

/** Route guard: renders the page only when the person holds the capability. */
const guard = (allowed: boolean, page: ReactNode) => (allowed ? page : <Navigate to="/dashboard" replace />)

function Shell({ user }: { user: User }) {
  if (user.forcePasswordChange) return <PasswordChangePage />
  const caps = user.capabilities

  return (
    <div dir="rtl" lang="fa">
      <SidebarProvider>
        <AppSidebar user={user} side="right" />
        <SidebarInset>
          <Header user={user} />
          <main className="px-4 pt-6 pb-16 md:px-8 md:pt-8">
          <Routes>
            <Route path="dashboard" element={<HomePage user={user} />} />
            <Route path="change-password" element={<PasswordChangePage />} />
            <Route path="allocations" element={guard(caps.submitsAllocation || caps.admin, <AllocationsPage user={user} />)} />
            <Route path="allocations/current" element={guard(caps.submitsAllocation, <CurrentAllocation />)} />
            <Route path="allocations/:id" element={<AllocationPage user={user} />} />
            <Route path="approvals/:kind" element={guard(caps.admin || caps.approvesDivisions.length > 0 || caps.approvesUnits.length > 0, <ApprovalPage />)} />
            <Route path="payroll" element={guard(caps.finance, <PayrollPage />)} />
            <Route path="analytics/pnl" element={guard(caps.companyReports, <PnlPage user={user} />)} />
            <Route path="planning/fpa" element={guard(caps.companyReports, <FpaPage />)} />
            <Route path="finance/entry" element={guard(caps.finance, <FinanceEntryPage user={user} />)} />
            <Route path="finance/close" element={guard(caps.finance, <CloseMonitorPage user={user} />)} />
            <Route path="forecast" element={guard(caps.forecastUnits.length > 0, <UnitForecastPage user={user} />)} />
            <Route path="notifications" element={<Navigate to="/dashboard" replace />} />
            <Route path="admin/*" element={guard(caps.admin, <AdminPage />)} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}

function rolesOf(user: User) {
  const caps = user.capabilities
  return [
    ...(caps.admin ? ["مدیر سامانه"] : []),
    ...(user.roles.includes("cfo") ? ["مدیر مالی"] : []),
    ...(caps.board ? ["هیئت مدیره"] : []),
    ...(caps.approvesUnits.length ? ["مدیر کسب‌وکار"] : []),
    ...(caps.approvesDivisions.length ? ["مدیر واحد"] : []),
    ...(caps.submitsAllocation ? ["پرسنل"] : []),
  ]
}

/** Where «بازگشت» goes when there is no earlier page in this tab (e.g. a link opened directly). */
function parentOf(pathname: string) {
  if (/^\/allocations\/.+/.test(pathname)) return "/allocations"
  if (/^\/admin\/.+/.test(pathname)) return "/admin"
  return "/dashboard"
}

function Header({ user }: { user: User }) {
  const query = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [photoOpen, setPhotoOpen] = useState(false)
  const here = currentNav(user, location.pathname)
  // react-router numbers its history entries: idx > 0 means an earlier page of this app exists in the tab.
  const hasHistory = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0
  const showBack = hasHistory || location.pathname !== "/dashboard"
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const logout = useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => query.setQueryData(["me"], null),
  })

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur-sm supports-[backdrop-filter]:bg-background/75 md:px-6">
      <SidebarTrigger className="-ms-1" />
      {showBack && (
        <Button variant="ghost" size="sm" className="-ms-1 gap-1 px-2 text-muted-foreground hover:text-foreground" onClick={() => (hasHistory ? navigate(-1) : navigate(parentOf(location.pathname)))} aria-label="بازگشت به صفحه قبل" title="بازگشت به صفحه قبل">
          <ArrowRight className="size-4" aria-hidden />
          <span className="hidden sm:inline">بازگشت</span>
        </Button>
      )}
      <span aria-hidden className="h-5 w-px bg-border" />
      <nav aria-label="مسیر صفحه" className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
        {here ? (
          <>
            <span className="hidden shrink-0 text-muted-foreground sm:inline">{here.section}</span>
            <ChevronLeft aria-hidden className="hidden size-3.5 shrink-0 text-muted-foreground sm:inline" />
            <span className="truncate font-medium" aria-current="page">{here.item.label}</span>
          </>
        ) : <span className="truncate font-medium">سامانه سود و زیان پیشگام</span>}
      </nav>
      {tasks.data && (
        <span className="hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground lg:inline-flex" title="دوره‌ای که اکنون حسابش بسته می‌شود">
          <span aria-hidden className={cn("size-1.5 rounded-full", tasks.data.closed ? "bg-success" : "bg-amber-500")} />
          {tasks.data.closed ? "بسته شد:" : "در حال بستن:"} <span className="font-medium text-foreground">{fa(tasks.data.cycle.periodLabel)}</span>
        </span>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 ps-1.5" aria-label={`حساب کاربری ${user.nameFa}`}>
            <Avatar className="size-7">
              {user.profileImageUrl && <AvatarImage src={user.profileImageUrl} alt={user.nameFa} />}
              <AvatarFallback>{user.nameFa?.slice(0, 1) || "U"}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm md:inline">{user.nameFa}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="font-normal text-muted-foreground">{rolesOf(user).join("، ")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setPhotoOpen(true)}>
            <Camera className="size-4" aria-hidden />
            {user.profileImageUrl ? "تغییر عکس پروفایل" : "افزودن عکس پروفایل"}
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to="/change-password">
              <KeyRound className="size-4" />
              تغییر رمز عبور
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => logout.mutate()}>
            <LogOut className="size-4" />
            خروج
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>عکس پروفایل</DialogTitle>
            <DialogDescription>هر زمان می‌توانید عکس را عوض کنید. عکس به شکل مربع برش می‌خورد و کوچک می‌شود.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar className="size-20 text-2xl">
              {user.profileImageUrl && <AvatarImage src={user.profileImageUrl} alt="" />}
              <AvatarFallback>{user.nameFa?.slice(0, 1) || "U"}</AvatarFallback>
            </Avatar>
            <ProfilePictureUpload
              endpoint="/uploads/profile-picture"
              variant="default"
              label={user.profileImageUrl ? "انتخاب عکس جدید" : "انتخاب عکس"}
              onUploaded={(profileImageUrl) => query.setQueryData<User>(["me"], (old) => old ? { ...old, profileImageUrl } : old)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPhotoOpen(false)}>بستن</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  )
}

function PasswordChangePage() {
  const query = useQueryClient()
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const change = useMutation({
    mutationFn: () => api<void>("/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
    onSuccess: () => query.setQueryData(["me"], null),
  })

  return (
    <main className="grid min-h-svh place-items-center bg-muted p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>انتخاب رمز عبور جدید</CardTitle>
          <CardDescription>برای ادامه باید رمز عبور موقت خود را تغییر دهید.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(event) => submit(event, () => change.mutate())}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="current-password">رمز عبور فعلی</FieldLabel>
                <Input id="current-password" type="password" dir="ltr" className="text-left" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(cleanPassword(event.target.value))} />
              </Field>
              <Field>
                <FieldLabel htmlFor="new-password">رمز عبور جدید</FieldLabel>
                <Input id="new-password" type="password" dir="ltr" className="text-left" autoComplete="new-password" minLength={8} value={newPassword} onChange={(event) => setNewPassword(cleanPassword(event.target.value))} />
              </Field>
              {change.error && <FieldError>{messageFor(change.error)}</FieldError>}
              <Button disabled={change.isPending || newPassword.length < 8}>ثبت رمز عبور</Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}

function AllocationsPage({ user }: { user: User }) {
  return (
    <>
      {user.capabilities.admin && <div className="mx-auto mb-6 w-full max-w-7xl"><AdminAllocationBrowser /></div>}
      {user.capabilities.submitsAllocation && <AllocationHistoryPage />}
    </>
  )
}

function AdminAllocationBrowser() {
  type AdminMonth = { year: number; month: number }
  type AdminEmployee = { id: number; nameFa: string; division?: { nameFa: string } | null; allocationId: number; status: string; percentage: string }
  const navigate = useNavigate()
  const organization = useQuery({ queryKey: ["organization"], queryFn: () => api<Organization>("/organization") })
  const months = useQuery({ queryKey: ["admin-allocation-months"], queryFn: () => api<AdminMonth[]>("/allocations/admin/months") })
  const [businessUnitId, setBusinessUnitId] = useState("")
  const [monthKey, setMonthKey] = useState("")
  const [employeeId, setEmployeeId] = useState("")

  useEffect(() => {
    if (!businessUnitId && organization.data?.businessUnits.length) setBusinessUnitId(String(organization.data.businessUnits[0]!.id))
  }, [businessUnitId, organization.data])

  useEffect(() => {
    if (!monthKey && months.data?.length) {
      const latest = months.data[0]!
      setMonthKey(`${latest.year}-${latest.month}`)
    }
  }, [monthKey, months.data])

  useEffect(() => {
    setEmployeeId("")
  }, [businessUnitId, monthKey])

  const selectedMonth = monthKey ? monthKey.split("-").map(Number) as [number, number] : null
  const employees = useQuery({
    queryKey: ["admin-allocation-employees", businessUnitId, selectedMonth?.[0], selectedMonth?.[1]],
    queryFn: () => api<AdminEmployee[]>(`/allocations/admin/employees?businessUnitId=${businessUnitId}&year=${selectedMonth![0]}&month=${selectedMonth![1]}`),
    enabled: Boolean(businessUnitId && selectedMonth),
  })
  const selectedEmployee = employees.data?.find((item) => String(item.id) === employeeId)

  return (
    <Card>
      <CardHeader>
        <CardTitle>مشاهده تخصیص واحدها برای مدیر سامانه</CardTitle>
        <CardDescription>ابتدا کسب‌وکار و ماه را انتخاب کنید، سپس فرد موردنظر را برای مشاهده تخصیص باز کنید.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <MonthPicker
          options={(months.data ?? []).map((item) => ({ year: item.year, month: item.month }))}
          value={selectedMonth ? { year: selectedMonth[0], month: selectedMonth[1] } : null}
          onChange={(value) => setMonthKey(`${value.year}-${value.month}`)}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <Select value={businessUnitId} onValueChange={setBusinessUnitId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="انتخاب کسب‌وکار" />
            </SelectTrigger>
            <SelectContent align="end">
              {organization.data?.businessUnits.map((unit) => (
                <SelectItem key={unit.id} value={String(unit.id)}>{unit.nameFa}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={employeeId} onValueChange={setEmployeeId} disabled={!employees.data?.length}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="انتخاب فرد" />
            </SelectTrigger>
            <SelectContent align="end">
              {employees.data?.map((employee) => (
                <SelectItem key={employee.id} value={String(employee.id)}>
                  {employee.nameFa}{employee.division?.nameFa ? ` (${employee.division.nameFa})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {employees.data && employees.data.length === 0 ? <EmptyState text="برای این کسب‌وکار و ماه، تخصیصی ثبت نشده است." /> : null}
        {selectedEmployee ? (
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="font-medium">{selectedEmployee.nameFa}</p>
              <p className="text-sm text-muted-foreground">
                سهم در کسب‌وکار انتخاب‌شده: {Number(selectedEmployee.percentage).toLocaleString("fa-IR", { style: "percent" })}، وضعیت: {labelFor(selectedEmployee.status)}
              </p>
            </div>
            <Button onClick={() => navigate(`/allocations/${selectedEmployee.allocationId}`)}>مشاهده تخصیص</Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function PnlPage({ user }: { user: User }) {
  const dashboard = useQuery({ queryKey: ["dashboard"], queryFn: () => api<Dashboard>("/dashboard") })
  const [entity, setEntity] = useEntity()
  const [selectedFy, setSelectedFy] = useState("")
  // Default to the fiscal year of the last closed month: at the start of a year the new one has no actuals yet.
  const defaultFy = dashboard.data ? fiscalYearFor(dashboard.data.currentMonth.month === 1 ? dashboard.data.currentMonth.year - 1 : dashboard.data.currentMonth.year, dashboard.data.currentMonth.month === 1 ? 12 : dashboard.data.currentMonth.month - 1) : null
  const fiscalYear = selectedFy ? Number(selectedFy) : defaultFy
  const report = useQuery({
    queryKey: ["pnl-fiscal-year", fiscalYear, entity],
    queryFn: () => api<PnlFiscalYear | null>(`/reports/pnl/fiscal-year/${fiscalYear}?entity=${entity}`),
    enabled: Boolean(fiscalYear),
  })
  const rows = report.data?.rows ?? []
  const month = dashboard.data?.currentMonth
  return (
    <Page
      title="سود و زیان"
      description={entity === "faraz" ? "سود و زیان جداگانه پایش سرمایه فراز." : "سود و زیان تجمیعی پیشگام، بدون پایش سرمایه فراز."}
      actions={user.capabilities.salaryDetail && month && (
        <Button variant="outline" asChild>
          <a href={`/api/v1/reports/pnl/${month.year}/${month.month}/excel?entity=${entity}`} download>
            <Download className="size-4" aria-hidden /> اکسل {formatMonthYear(month.year, month.month)}
          </a>
        </Button>
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <EntitySwitch value={entity} onChange={setEntity} />
        {fiscalYear && (
          <YearPicker years={fiscalYears(defaultFy ?? fiscalYear)} value={fiscalYear} onChange={(year) => setSelectedFy(String(year))} label={fiscalChip} ariaLabel="سال مالی" />
        )}
      </div>
      {!report.data ? (report.isLoading ? <LoadingCard /> : <Card><CardContent><EmptyState text="برای این سال مالی برنامه‌ای ثبت نشده است." /></CardContent></Card>) : (
        <>
          <PnlScorecard plan={report.data.totals.varianceForecast} actual={report.data.totals.varianceActual} period={comparablePeriod(rows)} />
          <ProfitBridge plan={report.data.totals.varianceForecast} actual={report.data.totals.varianceActual} />
          <MonthlyProfitChart rows={rows} />
          <PnlComparisonTable rows={rows} />
        </>
      )}
    </Page>
  )
}

/** Monthly plan vs actual, spreadsheet style: subjects as rows, months as columns (billion rial). */
function PnlComparisonTable({ rows }: { rows: PnlFiscalYear["rows"] }) {
  const series: Array<{ title: string; tone: string; value: (row: PnlFiscalYear["rows"][number]) => string | null; strong?: boolean }> = [
    { title: "درآمد — پیش‌بینی", tone: "text-blue-700", value: (row) => row.forecast.revenue },
    { title: "درآمد — محقق", tone: "text-blue-700", value: (row) => row.actual.revenue, strong: true },
    { title: "هزینه — پیش‌بینی", tone: "text-rose-700", value: (row) => row.forecast.totalCost },
    { title: "هزینه — محقق", tone: "text-rose-700", value: (row) => row.actual.totalCost, strong: true },
    { title: "سود / زیان — پیش‌بینی", tone: "text-emerald-700", value: (row) => row.forecast.profit },
    { title: "سود / زیان — محقق", tone: "text-emerald-700", value: (row) => row.actual.profit, strong: true },
    { title: "انحراف سود", tone: "", value: (row) => (row.comparable ? row.variance.profit : null) },
  ]
  return (
    <Card>
      <CardHeader>
        <CardTitle>جدول مقایسه ماهانه</CardTitle>
        <CardDescription>ارقام به میلیون ریال.</CardDescription>
      </CardHeader>
      <CardContent>
        {!rows.length ? <EmptyState text="داده‌ای برای گزارش پیدا نشد." /> : (
          <SheetTable label="مقایسه ماهانه پیش‌بینی و عملکرد">
            <thead>
              <tr>
                <th className={cn(sheet.corner, "min-w-44")}>ردیف</th>
                {rows.map((row) => <th key={`${row.year}-${row.month}`} className={cn(sheet.th, "min-w-20")}>{monthLabel(row.year, row.month)}</th>)}
              </tr>
            </thead>
            <tbody>
              {series.map((item) => (
                <tr key={item.title}>
                  <th scope="row" className={cn(sheet.rowHead, item.tone, item.strong && "font-semibold")}>{item.title}</th>
                  {rows.map((row) => {
                    const value = item.value(row)
                    return <td key={`${row.year}-${row.month}`} className={cn("border border-neutral-300 px-2 py-1 text-center tabular-nums", item.strong && "font-semibold", value !== null && BigInt(value) < 0n && "text-loss")}>{value === null ? "—" : moneyInMillions(value)}</td>
                  })}
                </tr>
              ))}
            </tbody>
          </SheetTable>
        )}
      </CardContent>
    </Card>
  )
}

function FpaPage() {
  const dashboard = useQuery({ queryKey: ["dashboard"], queryFn: () => api<Dashboard>("/dashboard") })
  const defaultFy = dashboard.data ? fiscalYearFor(dashboard.data.currentMonth.year, dashboard.data.currentMonth.month) : 1404
  const [selectedFy, setSelectedFy] = useState("")
  const fy = selectedFy ? Number(selectedFy) : defaultFy
  const [entity, setEntity] = useEntity()
  const report = useQuery({ queryKey: ["fpa", fy, entity], queryFn: () => api<FpaAnalysis | null>(`/reports/fpa/${fy}/analysis?entity=${entity}`) })

  useEffect(() => {
    if (!selectedFy) setSelectedFy(String(defaultFy))
  }, [defaultFy, selectedFy])

  return (
    <Page
      title="بودجه و برنامه مالی سالانه"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <YearPicker years={fiscalYears(defaultFy)} value={fy} onChange={(year) => setSelectedFy(String(year))} label={fiscalChip} ariaLabel="سال مالی" />
        <EntitySwitch value={entity} onChange={setEntity} />
      </div>
      {!report.data ? <EmptyState text="هنوز برنامه مالی برای این سال ثبت نشده است." /> : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <PlanDonut title="برنامه کل سال" description="ارقام پیش‌بینی هر دوازده ماه" figure={report.data.consolidated.fy} />
            <PlanDonut title="محقق‌شده تا امروز" description="ارقام واقعی ماه‌های تکمیل‌شده" figure={report.data.consolidated.fyRealized} />
          </div>
          <FpaPlanTable rows={report.data.bus} />
        </>
      )}
    </Page>
  )
}

function FpaPlanTable({ rows }: { rows: FpaAnalysis["bus"] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>جدول برنامه مالی کسب‌وکارها</CardTitle>
        <CardDescription>ارقام به میلیون ریال.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table className="min-w-[980px] text-right text-xs md:text-sm">
            <TableHeader>
              <TableRow className="border-b-0 bg-primary/10">
                <TableHead className="w-48 px-2 text-right align-middle text-primary" rowSpan={2}>کسب‌وکار</TableHead>
                <TableHead className="px-2 text-center text-blue-700" colSpan={3}>برنامه کل سال (پیش‌بینی)</TableHead>
                <TableHead className="px-2 text-center text-amber-700" colSpan={3}>پیش‌بینی باقیمانده سال</TableHead>
                <TableHead className="px-2 text-center text-emerald-700" colSpan={3}>محقق‌شده تا امروز</TableHead>
              </TableRow>
              <TableRow className="bg-muted/40">
                <TableHead className="px-2 text-right">درآمد</TableHead>
                <TableHead className="px-2 text-right">هزینه</TableHead>
                <TableHead className="px-2 text-right">سود</TableHead>
                <TableHead className="px-2 text-right">درآمد</TableHead>
                <TableHead className="px-2 text-right">هزینه</TableHead>
                <TableHead className="px-2 text-right">سود</TableHead>
                <TableHead className="px-2 text-right">درآمد</TableHead>
                <TableHead className="px-2 text-right">هزینه</TableHead>
                <TableHead className="px-2 text-right">سود</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length ? rows.map((row) => (
                <TableRow key={row.businessUnitId}>
                  <TableCell className="px-2 font-medium whitespace-nowrap">{row.businessUnitName}</TableCell>
                  <TableCell className="bg-blue-50/60 px-2 text-right tabular-nums">{moneyInMillions(row.fy.revenue)}</TableCell>
                  <TableCell className="bg-blue-50/60 px-2 text-right tabular-nums">{moneyInMillions(row.fy.totalCost)}</TableCell>
                  <TableCell className="bg-blue-50/60 px-2 text-right tabular-nums font-medium">{moneyInMillions(row.fy.profit)}</TableCell>
                  <TableCell className="bg-amber-50/70 px-2 text-right tabular-nums">{moneyInMillions(row.fyForecast.revenue)}</TableCell>
                  <TableCell className="bg-amber-50/70 px-2 text-right tabular-nums">{moneyInMillions(row.fyForecast.totalCost)}</TableCell>
                  <TableCell className="bg-amber-50/70 px-2 text-right tabular-nums font-medium">{moneyInMillions(row.fyForecast.profit)}</TableCell>
                  <TableCell className="bg-emerald-50/60 px-2 text-right tabular-nums">{moneyInMillions(row.fyRealized.revenue)}</TableCell>
                  <TableCell className="bg-emerald-50/60 px-2 text-right tabular-nums">{moneyInMillions(row.fyRealized.totalCost)}</TableCell>
                  <TableCell className="bg-emerald-50/60 px-2 text-right tabular-nums font-medium">{moneyInMillions(row.fyRealized.profit)}</TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={10}><EmptyState text="ردیفی برای کسب‌وکارها پیدا نشد." /></TableCell>
                </TableRow>
              )}
              </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function NotificationsPage() {
  const query = useQuery({ queryKey: ["notifications"], queryFn: () => api<Array<{ id: number; titleFa: string; bodyFa: string; link: string; isRead: boolean; createdAt: string }>>("/notifications") })
  const client = useQueryClient()
  const read = useMutation({ mutationFn: () => api<void>("/notifications/read-all", { method: "POST" }), onSuccess: () => client.invalidateQueries({ queryKey: ["notifications"] }) })

  return (
    <Page title="اعلان‌ها" description="پیام‌های سامانه و به‌روزرسانی‌های جریان کار.">
      <div className="flex justify-end"><Button variant="outline" onClick={() => read.mutate()}>خواندن همه</Button></div>
      <div className="grid gap-3">
        {query.data?.length ? query.data.map((item) => (
          <Card key={item.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{item.titleFa}</CardTitle>
                  <CardDescription>{item.bodyFa}</CardDescription>
                </div>
                {!item.isRead && <Badge><Bell className="mr-1 size-3" />جدید</Badge>}
              </div>
            </CardHeader>
          </Card>
        )) : <EmptyState text="اعلانی وجود ندارد." />}
      </div>
    </Page>
  )
}

function AdminPage() {
  const location = useLocation()
  const client = useQueryClient()
  const mode = location.pathname.endsWith("/organization") ? "organization" : location.pathname.endsWith("/audit") ? "audit" : location.pathname.endsWith("/reissue") ? "reissue" : location.pathname.endsWith("/settings") ? "settings" : "users"
  const audit = useQuery({ queryKey: ["admin-audit"], queryFn: () => api<Array<{ id: string; action: string; entityType: string; entityId: string; createdAt: string; actor: { nameFa: string } }>>("/admin/audit"), enabled: mode === "audit" })

  return (
    <Page title="مدیریت" description="کاربران و نقش‌ها، ساختار سازمان و مدیران، مهلت‌های فرایند و گزارش تغییرات.">
      <nav aria-label="بخش‌های مدیریت" className="-mb-2 flex gap-1 overflow-x-auto border-b">
        {([["users", "/admin", "کاربران"], ["organization", "/admin/organization", "سازمان"], ["settings", "/admin/settings", "مهلت‌ها"], ["audit", "/admin/audit", "گزارش تغییرات"]] as const).map(([key, to, label]) => {
          const current = mode === key || (key === "users" && mode === "reissue")
          return (
            <NavLink key={key} end={key === "users"} to={to} aria-current={current ? "page" : undefined}
              className={cn("-mb-px border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", current ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
              {label}
            </NavLink>
          )
        })}
      </nav>
      {mode === "users" && <AdminUsers />}
      {mode === "reissue" && <ReissueCredentials />}
      {mode === "organization" && <OrganizationAdmin />}
      {mode === "settings" && <ProcessSettingsAdmin />}
      {mode === "audit" && (
        audit.isLoading ? <LoadingCard /> : (
          <DataCard headers={["زمان", "کاربر", "عملیات", "موضوع", "شناسه"]} empty="هنوز تغییری ثبت نشده است.">
            {audit.data?.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-muted-foreground tabular-nums">{new Date(item.createdAt).toLocaleString("fa-IR", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                <TableCell className="font-medium">{item.actor.nameFa}</TableCell>
                <TableCell>{AUDIT_ACTION[item.action] ?? item.action}</TableCell>
                <TableCell>{AUDIT_ENTITY[item.entityType] ?? item.entityType}</TableCell>
                <TableCell className="text-muted-foreground tabular-nums">{fa(item.entityId)}</TableCell>
              </TableRow>
            ))}
          </DataCard>
        )
      )}
    </Page>
  )
}

const AUDIT_ACTION: Record<string, string> = {
  create: "ایجاد", update: "ویرایش", delete: "حذف", reset_password: "بازنشانی رمز عبور", reissue: "صدور مجدد اطلاعات ورود",
  activate: "فعال‌سازی", deactivate: "غیرفعال‌سازی", settings: "تغییر تنظیمات", close: "بستن دوره", reopen: "بازگشایی دوره",
}
const AUDIT_ENTITY: Record<string, string> = {
  user: "کاربر", division: "واحد", business_unit: "کسب‌وکار", settings: "مهلت‌ها", process_settings: "مهلت‌ها", period: "دوره", fpa_plan: "برنامه مالی", fpa_line: "ردیف برنامه",
}

function Status({ value }: { value: string }) {
  const variant = value === "approved" ? "default" : value === "rejected" ? "destructive" : "secondary"
  return <Badge variant={variant}>{labelFor(value)}</Badge>
}

function labelFor(value: string) {
  return statusLabel[value] ?? value
}

function sumAllocation(allocation: Allocation) {
  return allocation.lines.reduce((sum, line) => sum + Number(line.percentage) * 100, 0)
}

